import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grow,
  Fade,
  Avatar,
  Checkbox,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import CLogo from "../../Components/Reusable/Logo";
import { confirmFacebookPages } from "../../Action/api";

// The backend redirects here after Facebook OAuth with ?clientId=...&pages=[...]
// (see facebook-oauth callback -> successRedirectUrl). Each page entry looks like:
// { accountId, pageId, pageName, appLink, status }. accountId (not pageId) is the
// identifier the confirm API expects back.
const getPageName = (page) => page?.pageName || page?.name || "Untitled Page";
const getPageId = (page) => page?.accountId ?? page?.id ?? page?.pageId ?? getPageName(page);
const getPagePicture = (page) => page?.picture?.data?.url || page?.picture || null;

export const FacebookSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { pages, parseError } = useMemo(() => {
    const raw = searchParams.get("pages");
    if (!raw) return { pages: [], parseError: false };
    try {
      const parsed = JSON.parse(raw);
      return { pages: Array.isArray(parsed) ? parsed : [], parseError: false };
    } catch (error) {
      console.error("Error parsing Facebook pages from redirect URL:", error);
      return { pages: [], parseError: true };
    }
  }, [searchParams]);

  const [selectedPageIds, setSelectedPageIds] = useState(() => pages.map(getPageId));
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  const togglePage = (pageId) => {
    setSelectedPageIds((prev) =>
      prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId],
    );
  };

  const handleConfirm = async () => {
    if (confirming) return;
    setConfirmError("");
    setConfirming(true);
    try {
      const result = await confirmFacebookPages(selectedPageIds);
      if (result.success) {
        navigate("/dashboard", { state: { fbPagesConnected: true } });
      } else {
        setConfirmError(result.message || "Failed to confirm Facebook Page(s). Please try again.");
        setConfirming(false);
      }
    } catch (error) {
      setConfirmError("Something went wrong while confirming your Facebook Page(s). Please try again.");
      setConfirming(false);
    }
  };

  const hasPages = pages.length > 0;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        px: 2,
        py: 4,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      <Fade in timeout={500}>
        <Box>
          <CLogo showText={true} size="52px" textSize="26px" />
        </Box>
      </Fade>

      <Grow in timeout={450}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 480,
            borderRadius: 4,
            overflow: "hidden",
            bgcolor: "#fff",
            boxShadow: "0 32px 64px rgba(0,0,0,0.25)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              background: "linear-gradient(135deg,#10b981,#059669)",
              py: 5,
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -30,
                right: -30,
                width: 140,
                height: 140,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.08)",
              }}
            />
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 88,
                height: 88,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.15)",
                mb: 1.5,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "#fff" }} />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -4,
                  right: -4,
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "#1877F2",
                  border: "2.5px solid #fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FacebookRoundedIcon sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
            </Box>
            <Typography variant="h5" fontWeight={700} color="#fff">
              Facebook Connected!
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
              {hasPages
                ? "Choose which Page(s) you'd like to use with OruClick"
                : "Your account has been linked successfully"}
            </Typography>
          </Box>

          <Box sx={{ px: 3.5, py: 3.5 }}>
            {parseError && (
              <Alert severity="error" sx={{ mb: 2.5 }}>
                We couldn't read the Pages returned by Facebook. Please try connecting again.
              </Alert>
            )}

            {confirmError && (
              <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setConfirmError("")}>
                {confirmError}
              </Alert>
            )}

            {hasPages && (
              <Box sx={{ mb: 3 }}>
                {pages.map((page) => {
                  const pageId = getPageId(page);
                  const isSelected = selectedPageIds.includes(pageId);
                  const picture = getPagePicture(page);

                  return (
                    <Box
                      key={pageId}
                      onClick={() => togglePage(pageId)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2.5,
                        border: "1.5px solid",
                        borderColor: isSelected ? "#1877F2" : "#e2e8f0",
                        bgcolor: isSelected ? "rgba(24,119,242,0.06)" : "#fff",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:last-of-type": { mb: 0 },
                      }}
                    >
                      <Avatar src={picture || undefined} sx={{ bgcolor: "#1877F2", width: 44, height: 44 }}>
                        <FacebookRoundedIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <Typography variant="body1" fontWeight={600} noWrap>
                          {getPageName(page)}
                        </Typography>
                        {page?.category && (
                          <Typography variant="caption" sx={{ color: "#64748b" }}>
                            {page.category}
                          </Typography>
                        )}
                      </Box>
                      <Checkbox checked={isSelected} onChange={() => togglePage(pageId)} onClick={(e) => e.stopPropagation()} />
                    </Box>
                  );
                })}
              </Box>
            )}

            {!hasPages && !parseError && (
              <Typography variant="body2" sx={{ color: "#64748b", mb: 3, lineHeight: 1.6 }}>
                You can now schedule and publish posts to your Facebook Page
                directly from OruClick, right alongside your other connected
                accounts.
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              endIcon={confirming ? null : <ArrowForwardIcon />}
              startIcon={confirming ? <CircularProgress size={18} color="inherit" /> : null}
              disabled={confirming || (hasPages && selectedPageIds.length === 0)}
              onClick={hasPages ? handleConfirm : () => navigate("/dashboard")}
              sx={{
                py: 1.4,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
                fontSize: "1rem",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 14px rgba(102, 126, 234, 0.45)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6fd6 0%, #6a3d9a 100%)",
                  boxShadow: "0 6px 20px rgba(102, 126, 234, 0.55)",
                },
                "&.Mui-disabled": {
                  background: "#cbd5e1",
                },
              }}
            >
              {hasPages ? (confirming ? "Confirming..." : "Confirm & Continue") : "Go to Dashboard"}
            </Button>
          </Box>
        </Box>
      </Grow>
    </Box>
  );
};

export default FacebookSuccess;
